import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollPicker from './ScrollPicker';
import { LanguageToggleDark } from '@/components/feature/LanguageToggle';

interface BirthTimeInputProps {
  onNext: (data: { hour: number; minute: number; unknownTime: boolean }) => void;
  onBack: () => void;
  defaultHour?: number;
  defaultMinute?: number;
}

export default function BirthTimeInput({ onNext, onBack, defaultHour = 12, defaultMinute = 0 }: BirthTimeInputProps) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const ampmItems = [t('time.am'), t('time.pm')];
  const hoursItems = Array.from({ length: 12 }, (_, i) => isEn ? `${i + 1}` : `${i + 1}시`);
  const minutesItems = Array.from({ length: 60 }, (_, i) => isEn ? `${String(i).padStart(2,'0')}` : `${String(i).padStart(2,'0')}분`);

  const initAmpm = defaultHour >= 12 ? 1 : 0;
  const initHour = defaultHour % 12 === 0 ? 11 : (defaultHour % 12) - 1;

  const [ampmIdx, setAmpmIdx] = useState(initAmpm);
  const [hourIdx, setHourIdx] = useState(initHour);
  const [minuteIdx, setMinuteIdx] = useState(defaultMinute);
  const [showUnknownModal, setShowUnknownModal] = useState(false);

  const get24Hour = () => {
    const h12 = hourIdx + 1;
    if (ampmIdx === 0) return h12 === 12 ? 0 : h12;
    return h12 === 12 ? 12 : h12 + 12;
  };

  const h24 = get24Hour();
  const displayHour = h24 === 0
    ? t('time.midnight')
    : isEn
      ? `${String(h24).padStart(2,'0')}:${String(minuteIdx).padStart(2,'0')}`
      : `${String(h24).padStart(2,'0')}시 ${String(minuteIdx).padStart(2,'0')}분`;

  return (
    <div className="min-h-dvh flex flex-col bg-white" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* 헤더 고정 영역 */}
      <div className="px-5 pt-5 pb-0 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <button onClick={onBack} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer mr-2">
              <i className="ri-arrow-left-line text-gray-700" />
            </button>
            <img src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png" alt="바른사주" className="h-7 object-contain" />
          </div>
          <LanguageToggleDark />
        </div>
        <div className="flex gap-1.5">
          {[0,1,2,3].map(i => <div key={i} className={`h-1 flex-1 rounded-full ${i <= 1 ? 'bg-gray-900' : 'bg-gray-200'}`} />)}
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
        <div className="max-w-sm mx-auto w-full">
          <p className="text-gray-400 text-sm mb-1.5">{t('time.step')}</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 leading-snug whitespace-pre-line">{t('time.title')}</h2>

          <div className="flex items-start gap-2 bg-amber-50 rounded-xl p-3 mb-5 border border-amber-100">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-amber-500 text-sm" />
            </div>
            <p className="text-amber-700 text-xs leading-relaxed">{t('time.hint')}</p>
          </div>

          {/* 시간 피커 */}
          <div className="flex gap-2 mb-4">
            <div className="w-20 flex-shrink-0">
              <p className="text-xs text-gray-400 text-center mb-1.5">{t('time.labelAmpm')}</p>
              <ScrollPicker items={ampmItems} selectedIndex={ampmIdx} onChange={setAmpmIdx} itemHeight={48} visibleCount={3} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-400 text-center mb-1.5">{t('time.labelHour')}</p>
              <ScrollPicker items={hoursItems} selectedIndex={hourIdx} onChange={setHourIdx} itemHeight={48} visibleCount={3} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-400 text-center mb-1.5">{t('time.labelMinute')}</p>
              <ScrollPicker items={minutesItems} selectedIndex={minuteIdx} onChange={setMinuteIdx} itemHeight={48} visibleCount={3} />
            </div>
          </div>

          {/* 미리보기 */}
          <div className="bg-gray-50 rounded-2xl p-3.5 text-center">
            <span className="text-gray-900 font-bold text-base">{displayHour}</span>
          </div>
        </div>
      </div>

      {/* 고정 하단 버튼 */}
      <div className="flex-shrink-0 px-5 pt-3 bg-white border-t border-gray-100" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.25rem)' }}>
        <div className="max-w-sm mx-auto w-full flex flex-col gap-2">
          <button
            onClick={() => onNext({ hour: get24Hour(), minute: minuteIdx, unknownTime: false })}
            className="w-full bg-gray-900 text-white font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-95 transition-all cursor-pointer whitespace-nowrap">
            {t('next')} <i className="ri-arrow-right-line text-sm" />
          </button>
          <button
            onClick={() => setShowUnknownModal(true)}
            className="w-full text-gray-400 text-sm py-2.5 rounded-2xl hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
            {t('time.unknown')}
          </button>
        </div>
      </div>

      {showUnknownModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowUnknownModal(false)} />
          <div className="relative bg-white w-full max-w-md rounded-t-3xl p-6" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 2rem)' }}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-2xl mb-3">
              <i className="ri-time-line text-amber-500 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('time.unknownModal.title')}</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-5">{t('time.unknownModal.body')}</p>
            <button onClick={() => { setShowUnknownModal(false); onNext({ hour: 0, minute: 0, unknownTime: true }); }}
              className="w-full bg-gray-900 text-white font-bold py-4 rounded-2xl cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-all">
              {t('time.unknownModal.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
